import { useMemo, useState } from "react";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameDay,
  isSameMonth,
  format,
  parseISO,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { CelebrationRow } from "@/hooks/queries";

// Google-Calendar-style month grid view of upcoming celebrations.
// Pairs with the list view in Dashboard.tsx, same data, different
// shape. The right detail pane keeps its current behaviour: clicking
// an event chip selects the celebration and updates the pane.
//
// Mon-Sun grid (Australian convention). 6 rows always visible so the
// height stays stable as you flip months. Out-of-month days render
// at low opacity so the eye anchors on the active month.
//
// Each event chip uses the same dot colour as the list view so the
// two views share a visual taxonomy: coral=birthday, amber=anniversary,
// sage=welcome, slate=other.

type CelebrationsByDay = Map<string, CelebrationRow[]>;

interface CalendarViewProps {
  celebrations: CelebrationRow[];
  selectedId: string | null;
  onSelect: (celebration: CelebrationRow) => void;
  onOpen?: (celebration: CelebrationRow) => void;
  today: Date;
}

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function CalendarView({ celebrations, selectedId, onSelect, onOpen, today }: CalendarViewProps) {
  // Default the visible month to whichever month contains the next
  // upcoming celebration (so the customer lands on something useful
  // even if they last switched to a quiet month).
  const [visibleMonth, setVisibleMonth] = useState<Date>(() => {
    const next = celebrations.find((c) => parseISO(c.date) >= today);
    return next ? startOfMonth(parseISO(next.date)) : startOfMonth(today);
  });

  const byDay: CelebrationsByDay = useMemo(() => {
    const m = new Map<string, CelebrationRow[]>();
    for (const c of celebrations) {
      if (!m.has(c.date)) m.set(c.date, []);
      m.get(c.date)!.push(c);
    }
    return m;
  }, [celebrations]);

  // Build the 6-row grid covering the visible month.
  const gridDays: Date[] = useMemo(() => {
    const monthStart = startOfMonth(visibleMonth);
    const monthEnd = endOfMonth(visibleMonth);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const days: Date[] = [];
    let d = gridStart;
    while (d <= gridEnd) {
      days.push(d);
      d = addDays(d, 1);
    }
    // Pad to 6 rows (42 cells) for stable height across short months.
    while (days.length < 42) {
      days.push(addDays(days[days.length - 1], 1));
    }
    return days;
  }, [visibleMonth]);

  return (
    <div className="px-8 py-2 2xl:px-10 2xl:py-4">
      {/* Header: month nav + today */}
      <div className="mb-4 flex items-center justify-between 2xl:mb-5">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setVisibleMonth((m) => subMonths(m, 1))}
            className="flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-[rgba(29,52,97,0.06)] 2xl:h-9 2xl:w-9"
            aria-label="Previous month"
          >
            <ChevronLeft size={15} style={{ color: "rgba(29,52,97,0.6)" }} />
          </button>
          <button
            onClick={() => setVisibleMonth((m) => addMonths(m, 1))}
            className="flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-[rgba(29,52,97,0.06)]"
            aria-label="Next month"
          >
            <ChevronRight size={15} style={{ color: "rgba(29,52,97,0.6)" }} />
          </button>
          <span
            className="ml-1 text-base font-semibold 2xl:text-lg"
            style={{ color: "rgba(29,52,97,0.92)" }}
          >
            {format(visibleMonth, "MMMM yyyy")}
          </span>
        </div>
        <button
          onClick={() => setVisibleMonth(startOfMonth(today))}
          className="rounded-md border px-2.5 py-1 text-xs font-medium transition-colors hover:bg-[rgba(29,52,97,0.03)]"
          style={{
            borderColor: "rgba(29,52,97,0.14)",
            color: "rgba(29,52,97,0.92)",
          }}
        >
          Today
        </button>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 border-b" style={{ borderColor: "rgba(29,52,97,0.08)" }}>
        {WEEKDAY_LABELS.map((label) => (
          <div
            key={label}
            className="py-2 text-center text-[10px] font-medium uppercase 2xl:py-2.5 2xl:text-xs"
            style={{
              fontFamily: "JetBrains Mono, monospace",
              letterSpacing: "0.08em",
              color: "rgba(29,52,97,0.45)",
            }}
          >
            {label}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div
        className="grid grid-cols-7 2xl:[grid-auto-rows:minmax(112px,1fr)]"
        style={{ gridAutoRows: "minmax(86px, 1fr)" }}
      >
        {gridDays.map((day) => {
          const dateKey = format(day, "yyyy-MM-dd");
          const events = byDay.get(dateKey) ?? [];
          const inMonth = isSameMonth(day, visibleMonth);
          const isToday = isSameDay(day, today);
          return (
            <div
              key={dateKey}
              className="border-b border-r p-1.5 2xl:p-2.5"
              style={{
                borderColor: "rgba(29,52,97,0.06)",
                background: isToday ? "rgba(196,86,74,0.04)" : "white",
                opacity: inMonth ? 1 : 0.4,
              }}
            >
              <div
                className="mb-1 flex h-5 items-center"
              >
                <span
                  className={`flex items-center justify-center rounded-full text-[11px] font-medium 2xl:text-sm ${
                    isToday ? "h-5 w-5 text-white 2xl:h-6 2xl:w-6" : ""
                  }`}
                  style={{
                    background: isToday ? "#C4564A" : "transparent",
                    color: isToday ? "white" : "rgba(29,52,97,0.55)",
                    fontFamily: "JetBrains Mono, monospace",
                  }}
                >
                  {format(day, "d")}
                </span>
              </div>
              <div className="space-y-1">
                {events.slice(0, 3).map((cel) => {
                  const isSelected = cel.id === selectedId;
                  return (
                    <button
                      key={cel.id}
                      onClick={() => onSelect(cel)}
                      onDoubleClick={() => onOpen?.(cel)}
                      className="block w-full truncate rounded px-1.5 py-0.5 text-left text-[10.5px] font-medium transition-all hover:translate-x-px 2xl:px-2 2xl:py-1 2xl:text-xs"
                      style={{
                        background: isSelected ? typeColor(cel.type) : `${typeColor(cel.type)}1A`,
                        color: isSelected ? "white" : typeColor(cel.type),
                        border: isSelected ? "none" : `1px solid ${typeColor(cel.type)}33`,
                      }}
                      title={`${memberName(cel)}, ${humanType(cel.type)}`}
                    >
                      {memberName(cel)}
                    </button>
                  );
                })}
                {events.length > 3 && (
                  <div
                    className="px-1.5 text-[10px]"
                    style={{ color: "rgba(29,52,97,0.45)" }}
                  >
                    +{events.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Local helpers, duplicated from Dashboard.tsx so this component
// stays portable. Worth extracting to a shared `concierge/lib.ts` if
// we add a third view (week, agenda, etc.) that needs them.

function memberName(c: CelebrationRow): string {
  const tm = c.team_members;
  if (!tm) return "Team member";
  if (Array.isArray(tm)) return tm[0]?.name || "Team member";
  return tm.name;
}

function humanType(type: string): string {
  if (type === "birthday") return "birthday";
  if (type === "work_anniversary") return "anniversary";
  if (type === "welcome") return "first day";
  if (type === "farewell") return "farewell";
  if (type === "milestone") return "milestone";
  return type;
}

function typeColor(type: string): string {
  if (type === "birthday") return "#C4564A";
  if (type === "work_anniversary") return "#C4943B";
  if (type === "welcome") return "#5B8C76";
  return "#6B7C9C";
}
